"use client";

import dynamic from "next/dynamic";
import Link from "next/link";
import { Calendar, ArrowLeft } from "lucide-react";
import { ComparisonData } from "./types";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import SpecRow from "./SpecRow";
import ProsConsSection from "./components/ProsConsSection";
import ComparisonEducation from "./ComparisonEducation";
import Accordion from "../ui/Accordion";
import RelatedTools from "../blog/RelatedTools";
import YouMightAlsoLike from "../blog/YouMightAlsoLike";
import SourcesAndMethodology from "../layout/SourcesAndMethodology";
import EeatTrustBadge from "../layout/EeatTrustBadge";
import { getCalculatorConfig } from "@/lib/constants/calculators";
import { comparisonsMap } from "@/data/comparisons";
import { getGlossaryTermsForComparison, getGlossaryTermsForCalculator } from "@/lib/glossary/related-terms";
import { formatISODate } from "@/lib/daily/dateUtils";
import { SITE_AUTHOR } from "@/lib/constants/eeat";

// Load ComparisonCalculator dynamically to prevent Chart.js from executing during static build time SSR
const ComparisonCalculator = dynamic(
  () => import("./ComparisonCalculator"),
  { ssr: false }
);

interface ComparisonTemplateProps {
  data: ComparisonData;
  // Related blog posts computed server-side and passed in
  relatedBlogPosts?: Array<{ title: string; slug: string; summary: string; images?: Array<{ url: string; alt: string }> }>;
  // "You Might Also Like" items computed server-side (the mapper's data pool
  // imports the full posts module, which must never enter a client bundle)
  ymalItems?: import("@/lib/blog/ymal-mapper").YouMightAlsoLikeItem[];
}

export default function ComparisonTemplate({ data, relatedBlogPosts = [], ymalItems = [] }: ComparisonTemplateProps) {
  const {
    title,
    description,
    lastUpdated,
    relatedSlugs,
    labelA,
    labelB,
    specs,
    prosA,
    consA,
    prosB,
    consB,
    verdictTitle,
    verdictSummary,
    bestForA,
    bestForB,
    faqs,
    education,
    countryCode,
    relatedCalculator,
  } = data;

  const calcConf = relatedCalculator ? getCalculatorConfig(relatedCalculator) : null;
  const isSupported = calcConf && (calcConf.countries === "all" || calcConf.countries.includes(countryCode.toLowerCase()));
  const calcPath = isSupported ? `/${countryCode.toLowerCase()}/${calcConf.category}/${relatedCalculator}` : null;

  // Build related tools array
  const resolvedRelated: Array<{ title: string; href: string; description: string; type: "calculator" | "comparison" | "glossary" | "guide" }> = [];
  
  if (relatedSlugs && relatedSlugs.length > 0) {
    relatedSlugs.forEach((slug) => {
      const compConf = comparisonsMap[slug];
      if (compConf) {
        resolvedRelated.push({
          title: compConf.title,
          href: `/compare/${slug}`,
          description: compConf.description,
          type: "comparison"
        });
      }
    });
  }

  // Add related calculator if exists
  if (calcConf && calcPath) {
    resolvedRelated.push({
      title: calcConf.name,
      href: calcPath,
      description: calcConf.description,
      type: "calculator"
    });
  }

  // Add individual glossary term links related to this comparison
  const compSlug = data.slug;
  const relatedGlossaryTerms = getGlossaryTermsForComparison(compSlug, countryCode.toLowerCase());
  for (const gt of relatedGlossaryTerms) {
    const href = `/${gt.country}/glossary/${gt.slug}`;
    if (!resolvedRelated.some(r => r.href === href)) {
      resolvedRelated.push({
        title: gt.term,
        href,
        description: gt.definition,
        type: "glossary"
      });
    }
  }
  // Also add glossary terms for the related calculator if it exists
  if (relatedCalculator) {
    const calcTerms = getGlossaryTermsForCalculator(relatedCalculator, countryCode.toLowerCase());
    for (const gt of calcTerms) {
      const href = `/${gt.country}/glossary/${gt.slug}`;
      if (!resolvedRelated.some(r => r.href === href)) {
        resolvedRelated.push({
          title: gt.term,
          href,
          description: gt.definition,
          type: "glossary"
        });
      }
    }
  }

  const formatDate = (dateStr: string) => {
    return formatISODate(dateStr);
  };

  return (
    <div className="comparison-template-container container">
      {/* Breadcrumb Navigation */}
      <Breadcrumbs />

      {/* Back link */}
      <div className="back-link-wrapper">
        <Link href="/compare" className="back-link">
          <ArrowLeft size={16} /> Back to All Comparisons
        </Link>
      </div>

      {/* Hero Header Section */}
      <header className="comparison-hero">
        <h1 className="hero-title">{title}</h1>
        <p className="hero-description">{description}</p>
      </header>

      {/* Interactive Simulator / Calculator Section */}
      <section className="simulator-section">
        <div className="simulator-header">
          <h2 className="section-title">Interactive Comparison Simulator</h2>
          <p className="section-description">
            Adjust the variables below to simulate outcomes, compare rates, and see real-time projections.
          </p>
        </div>
        <div className="simulator-container">
          <ComparisonCalculator data={data} />
        </div>
      </section>

      {/* Comparison Specifications Matrix */}
      <div className="section-divider"></div>
      <section className="section matrix-section">
        <h2 className="section-title">Side-by-Side Comparison</h2>
        <p className="section-intro">
          A direct comparison of features, rules, limits, and eligibility requirements.
        </p>
        <div className="matrix-table-wrapper">
          <table className="matrix-table">
            <thead>
              <tr>
                <th className="matrix-header-col">Feature / Detail</th>
                <th className="matrix-header-col">{labelA}</th>
                <th className="matrix-header-col">{labelB}</th>
              </tr>
            </thead>
            <tbody>
              {specs.map((spec, idx) => (
                <SpecRow key={idx} spec={spec} />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Pros and Cons Section */}
      <div className="section-divider"></div>
      <section className="section pros-cons-section">
        <h2 className="section-title">Pros & Cons Breakdown</h2>
        <p className="section-intro">
          Analyze the advantages and drawbacks of each financial product before making a decision.
        </p>
        <ProsConsSection
          labelA={labelA}
          labelB={labelB}
          prosA={prosA}
          consA={consA}
          prosB={prosB}
          consB={consB}
        />
      </section>

      {/* Humanized education / story section — the teaching layer with charts */}
      {education && education.length > 0 && (
        <>
          <div className="section-divider"></div>
          <ComparisonEducation sections={education} />
        </>
      )}

      {/* Editorial Verdict & Best-For Cards */}
      <div className="section-divider"></div>
      <section className="section verdict-section">
        <h2 className="section-title">The Verdict</h2>
        <div className="verdict-card">
          <h3 className="verdict-card-title">{verdictTitle}</h3>
          <p className="verdict-card-text">{verdictSummary}</p>
          
          <div className="best-for-grid">
            <div className="best-for-card card-a">
              <h4 className="best-for-title">Choose {labelA} if...</h4>
              <p className="best-for-text">{bestForA}</p>
            </div>
            <div className="best-for-card card-b">
              <h4 className="best-for-title">Choose {labelB} if...</h4>
              <p className="best-for-text">{bestForB}</p>
            </div>
          </div>
        </div>
      </section>

      {/* E-E-A-T Trust Badge */}
      <div className="section-divider"></div>
      <EeatTrustBadge
        label="Built & Maintained"
        authorName={data.authorName}
        authorCredentials={data.authorCredentials}
        lastUpdated={formatDate(lastUpdated)}
        style={{ marginTop: 0, marginBottom: 0 }}
      />

      {/* FAQ Section */}
      {faqs && faqs.length > 0 && (
        <>
          <div className="section-divider"></div>
          <section className="faq-section">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <Accordion items={faqs.map(faq => ({ title: faq.question, content: <p>{faq.answer}</p> }))} />
          </section>
        </>
      )}

      {/* Related Tools Section */}
      {resolvedRelated.length > 0 && (
        <>
          <div className="section-divider"></div>
          <RelatedTools 
            tools={resolvedRelated}
            postCountryCode={countryCode.toLowerCase()}
            customTitle="Related Tools, Comparisons & Glossary"
            customDescription="Explore related calculators, comparisons, and glossary terms:"
          />
        </>
      )}

      {/* You Might Also Like — blog posts related to this comparison */}
      {ymalItems.length > 0 && (
        <>
          <div className="section-divider"></div>
          <YouMightAlsoLike noMargin={true} items={ymalItems} />
        </>
      )}

      {/* E-E-A-T: primary-source citations + editorial process */}
      <div className="section-divider"></div>
      <div className="sources-block-wrapper" style={{ marginTop: 0 }}>
        <SourcesAndMethodology country={countryCode?.toLowerCase()} category={data.category} lastUpdated={lastUpdated} compact={true} />
      </div>

      {/* Comparison Disclaimer - Slanted Design */}
      <div className="section-divider"></div>
      <footer className="slanted-disclaimer">
        Disclaimer: The comparison data, simulator outputs, and projections on this page are provided for general informational and educational purposes only. They do not constitute financial, investment, tax, or legal advice. All values are estimates based on statutory data and hypothetical inputs. Interest rates, contribution limits, tax brackets, and regulatory rules change frequently and vary by jurisdiction. Always consult a qualified professional advisor and verify critical figures with official government publications before making any financial decisions.
      </footer>

      <style jsx>{`
        .comparison-template-container {
          padding-top: var(--space-6);
          padding-bottom: var(--space-12);
        }
        
        @media (max-width: 767px) {
          .comparison-template-container {
            padding-top: var(--space-4);
            padding-bottom: var(--space-8);
          }
        }
        
        .comparison-hero {
          margin-bottom: 0;
        }
        
        @media (max-width: 767px) {
          .comparison-hero {
            margin-bottom: 0;
          }
        }
        
        .hero-title {
          font-family: var(--font-heading);
          font-size: var(--text-3xl);
          color: var(--text-primary);
          font-weight: var(--fw-extrabold);
          letter-spacing: var(--tracking-tight);
          line-height: var(--lh-tight);
          margin-bottom: var(--space-3);
        }
        
        .hero-description {
          font-family: var(--font-body);
          font-size: var(--text-md);
          line-height: 1.6;
          color: var(--text-secondary);
          max-width: 100%;
          margin-bottom: var(--space-3);
          font-weight: var(--fw-medium);
          word-wrap: break-word;
        }
        
        @media (max-width: 767px) {
          .hero-description {
            font-size: var(--text-sm);
          }
        }

        .sources-block-wrapper section {
          margin-top: 0 !important;
        }
        .faq-section {
          margin-top: 0;
          margin-bottom: 0;
        }
        .slanted-disclaimer {
          padding: 0;
          margin: 0;
          font-size: var(--text-2xs);
          color: var(--text-muted, var(--text-secondary));
          line-height: 1.5;
          font-style: italic;
          background: transparent;
          border: none;
        }
        .section {
          margin-top: 0;
          margin-bottom: 0;
        }
        
        @media (max-width: 767px) {
          .section {
            margin-top: 0;
            margin-bottom: 0;
          }
        }
        
        .section-title {
          font-family: var(--font-heading);
          font-size: var(--text-2xl);
          color: var(--text-primary);
          font-weight: var(--fw-bold);
          margin-top: 0;
          margin-bottom: var(--space-4);
          letter-spacing: var(--tracking-tight);
        }
        
        .section-title:has(+ .section-intro),
        .section-title:has(+ .section-description) {
          margin-bottom: 0;
        }
        
        .section-intro {
          font-family: var(--font-body);
          font-size: var(--text-sm);
          color: var(--text-secondary);
          line-height: 1.6;
          margin-top: 0;
          margin-bottom: var(--space-5);
          font-weight: var(--fw-medium);
        }
        
        @media (max-width: 767px) {
          .section-intro {
            font-size: var(--text-sm);
            margin-top: 0;
            margin-bottom: var(--space-4);
          }
        }
        
        .simulator-section {
          margin-top: var(--space-4);
          margin-bottom: 0;
        }

        .simulator-header {
          text-align: left;
          margin-bottom: var(--space-6);
          max-width: 800px;
        }

        .section-description {
          font-size: var(--text-sm);
          color: var(--text-secondary);
          line-height: 1.6;
          font-weight: var(--fw-medium);
          margin-top: 0;
          margin-bottom: 0;
        }

        .simulator-container {
          background: linear-gradient(135deg, var(--secondary-a03) 0%, var(--primary-a03) 100%);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          padding: var(--space-6);
          box-shadow: var(--shadow-sm);
        }

        @media (max-width: 767px) {
          .simulator-container {
            padding: var(--space-4);
          }
        }
        .matrix-table-wrapper {
          background-color: var(--bg-surface);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          overflow-x: auto;
          box-shadow: var(--card-shadow);
        }
        .matrix-table {
          width: 100%;
          border-collapse: collapse;
          text-align: left;
        }
        .matrix-header-col {
          background-color: var(--btn-secondary-hover);
          border-bottom: 2px solid var(--border-color);
          padding: var(--space-4) var(--space-5);
          font-family: var(--font-heading);
          font-size: var(--text-sm);
          font-weight: var(--fw-extrabold);
          color: var(--text-primary);
        }
        
        @media (max-width: 767px) {
          .matrix-header-col {
            padding: var(--space-3) var(--space-3);
            font-size: var(--text-2xs);
          }
        }
        
        .matrix-header-col:first-child {
          width: 30%;
        }
        
        @media (max-width: 767px) {
          .matrix-header-col:first-child {
            width: 40%;
          }
        }
        
        .matrix-header-col:not(:first-child) {
          width: 35%;
        }
        
        @media (max-width: 767px) {
          .matrix-header-col:not(:first-child) {
            width: 30%;
          }
        }
        .verdict-card {
          background-color: var(--bg-surface);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          padding: var(--space-6);
          display: flex;
          flex-direction: column;
          gap: var(--space-5);
          box-shadow: var(--card-shadow);
        }
        
        @media (max-width: 767px) {
          .verdict-card {
            padding: var(--space-4);
            gap: var(--space-4);
          }
        }
        
        .verdict-card-title {
          font-family: var(--font-heading);
          font-size: var(--text-xl);
          color: var(--text-primary);
          font-weight: var(--fw-bold);
          line-height: var(--lh-snug);
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        .verdict-card-text {
          font-family: var(--font-body);
          font-size: var(--text-sm);
          color: var(--text-secondary);
          line-height: 1.6;
          font-weight: var(--fw-semibold);
        }
        .best-for-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--space-4);
          margin-top: var(--space-2);
        }
        @media (max-width: 768px) {
          .best-for-grid {
            grid-template-columns: 1fr;
          }
        }
        .best-for-card {
          padding: var(--space-5);
          border-radius: var(--radius-md);
          border: 1px solid var(--border-color);
          background-color: var(--bg-surface-hover);
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
        }
        
        @media (max-width: 767px) {
          .best-for-card {
            padding: var(--space-4);
          }
        }
        .card-a {
          border-left: 4px solid var(--info);
        }
        .card-b {
          border-left: 4px solid var(--primary);
        }
        .best-for-title {
          font-family: var(--font-heading);
          font-size: var(--text-sm);
          font-weight: var(--fw-extrabold);
          color: var(--text-primary);
        }
        .best-for-text {
          font-family: var(--font-body);
          font-size: var(--text-xs);
          color: var(--text-secondary);
          line-height: 1.5;
          font-weight: var(--fw-semibold);
        }
        .editorial-disclaimer-box {
          background-color: var(--orange-a03);
          border: 1px dashed var(--orange-a20);
          border-left: 4px solid var(--accent-warning);
          border-radius: var(--radius-sm);
          padding: var(--space-4) var(--space-5);
          color: var(--text-secondary);
        }
        .disclaimer-title {
          font-family: var(--font-heading);
          font-size: var(--text-xs);
          font-weight: var(--fw-extrabold);
          color: var(--text-primary);
          margin-bottom: var(--space-1);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .disclaimer-text {
          font-family: var(--font-body);
          font-size: var(--text-2xs);
          line-height: 1.5;
          color: var(--text-muted);
          font-weight: var(--fw-medium);
        }
      `}</style>
    </div>
  );
}
